import { ReadonlyVec2 } from 'gl-matrix';
import { VoxelSubRange } from '~/domain/services/imageService/gridGeometry';
import { PostProcessingMode } from '~/engine/util/algorithms/sdf/SDFPostProcessing';
import { Grid } from '../../../data/common/Grid';
import { TextureView } from '../../../view/TextureView';
import { Environment } from '~/engine/data/Environment';
import { PlaneView } from '../../../view/views/PlaneView';
import { enqueueSnackbar } from '~/utils/notifications';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { CrosshairInteractionMode, Tool } from '../../Tool';
import { rectangleToVoxelWindow } from '../../../view/picking/curveSelectionBox';
import { writeRefusal } from './editableView';
import { localization } from '~/i18n';
import { rectangleSelectAxis } from './obliqueGating';

/**
 * Options {@link RectangleSelectTool} is constructed with. Mirrors the
 * `tool: 'rectangle-select'` arm of {@link ToolConfig} in ToolManager.ts
 * (which additionally carries the base fields every tool config needs —
 * panZoom and views — that this tool itself does not use).
 */
export interface RectangleSelectToolOptions {
    tool: 'rectangle-select';
    /** The Environment whose views a drag may start on; `undefined`
     *  refuses every view. */
    environment: Environment | undefined;
    /** Controls how far the rectangle is extruded along the slice normal:
     *  one slice in 2D, the whole volume in 3D. */
    mode: PostProcessingMode;
    /**
     * The grid the selection is resolved against — the target ROI's own
     * texture grid, and the grid the reported window indexes.
     *
     * It supplies both halves of the geometry: which axis the drag plane's
     * normal runs along ({@link rectangleSelectAxis}), and which slice
     * along that normal the plane sits on, so "this slice" means the one
     * displayed rather than a depth in millimetres that has to be resolved
     * back to a slice afterwards.
     */
    grid: Grid;
    /** Called with the dragged rectangle as a voxel window once the drag
     *  completes. Does NOT perform the erase itself — the caller runs the
     *  SDF write through its own tracked-write path so undo and the
     *  reported outcome match every other cleanup operation. */
    onSelect: (range: VoxelSubRange) => void;
}

/**
 * Lets the user drag a rectangle over the viewport to pick curves of the
 * active ROI for deletion. Modeled on {@link BoxZoomTool}, the shipped
 * rubber-band tool, reusing its overlay sizing fix and its accidental-click
 * guard.
 *
 * ROIs here are SDF-backed: the visible curve is a marching-squares
 * extraction, not a pickable polygon, so a curve is picked by the voxels
 * the rectangle covers rather than by hit-testing a polygon. This tool
 * only converts the drag to a world box and hands it up via
 * {@link RectangleSelectToolOptions.onSelect} — it does not delete
 * anything (that is
 * {@link SDFOperations.applyClearPartsTouchingRect}) and it does not
 * write the texture (that is the panel's `useRoiSdfOperation` path).
 *
 * The crosshair follows the shared contouring policy: reduced to its
 * endpoint handles while the tool is active, blocked during a drag, so a
 * drag started over a crosshair line cannot move the line.
 *
 * A drag takes moves from the view it started on only: a move over another
 * view leaves the rectangle where the last move on its own view put it, and
 * the release resolves that rectangle wherever it happens.
 */
export class RectangleSelectTool extends Tool {
    public readonly toolType = 'rectangle-select';

    private readonly _mode: PostProcessingMode;
    private readonly _environment: Environment | undefined;
    private readonly _grid: Grid;
    private readonly _onSelect: (range: VoxelSubRange) => void;

    /** Canvas-pixel coordinates where the drag started. */
    private _startCanvasCoords: ReadonlyVec2 | null = null;
    /** Canvas-pixel coordinates of the last move on the drag's view. */
    private _endCanvasCoords: ReadonlyVec2 | null = null;
    /** The view on which the drag is happening. */
    private _activeView: PlaneView | null = null;
    /** 2D rendering context used to draw the selection rectangle. */
    private _ctx: CanvasRenderingContext2D | null = null;

    public constructor(options: RectangleSelectToolOptions) {
        super();
        this._mode = options.mode;
        this._environment = options.environment;
        this._grid = options.grid;
        this._onSelect = options.onSelect;
    }

    /**
     * Refuses at the PRESS, exactly where {@link BrushTool} refuses, and
     * warns with the reason ({@link refusalFor}): the drag never starts, so
     * there is no rubber-band overlay promising a selection that will not
     * be honoured, and no half-formed gesture to unwind on release. A
     * per-view decision, again like the brushes — the user may have rotated
     * one viewport while the other two are still sound, and only the view
     * the press landed on is disqualified.
     *
     * See {@link rectangleSelectAxis} for the geometric reason: an
     * oblique drag plane inflates the axis-aligned world box by roughly
     * `sin(theta) x drag size`, an over-delete an order of magnitude past
     * the spacing error and invisible to the user.
     */
    public onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (button !== 0 || !(view instanceof PlaneView)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const refusal = this.refusalFor(view);
        if (refusal !== undefined) {
            enqueueSnackbar(refusal, { variant: 'warning' });
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (rectangleSelectAxis(view, this._grid) !== undefined) {
            const canvasCoords = view.clientToCanvas(coordinates);
            this._startCanvasCoords = canvasCoords;
            this._endCanvasCoords = canvasCoords;
            this._activeView = view;

            const canvas2D = view.get2DCanvas;
            if (canvas2D) {
                // The 2D overlay canvas has no width/height attributes set
                // in JSX (defaults to 300x150). We must sync them with the
                // actual display size before drawing.
                canvas2D.width = canvas2D.offsetWidth;
                canvas2D.height = canvas2D.offsetHeight;
                this._ctx = canvas2D.getContext('2d');
            }

            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * A view of another environment, or with none armed, gets the
     * refusal of {@link writeRefusal}; a view whose plane is
     * not aligned with the patient axes of the ROI's grid the
     * `contouring.refused.rectangleNotAxisAligned` reason.
     */
    public override refusalFor(view: PlaneView): string | undefined {
        return writeRefusal(view, this._environment, (own) =>
            rectangleSelectAxis(own, this._grid) === undefined
                ? localization.t('contouring.refused.rectangleNotAxisAligned')
                : undefined
        );
    }

    public override isActionInProgress(): boolean {
        return this._startCanvasCoords !== null;
    }

    public override getDrawingView(): PlaneView | undefined {
        return this._activeView ?? undefined;
    }

    public override getCrosshairInteractionMode(
        isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.contouringCrosshairMode(
            isDrawingActiveInOtherView,
            this.isStrokeInProgress()
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        view: TextureView
    ): EventHandlerResult {
        if (this._startCanvasCoords && this._activeView) {
            if (view === this._activeView) {
                this._endCanvasCoords =
                    this._activeView.clientToCanvas(coordinates);
                this.drawSelectionRect(this._endCanvasCoords);
            }
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseRelease(
        _button: number,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (this._startCanvasCoords && this._activeView) {
            const endCanvasCoords =
                view === this._activeView
                    ? this._activeView.clientToCanvas(coordinates)
                    : (this._endCanvasCoords ?? this._startCanvasCoords);

            const range = this.rectangleToVoxelWindow(
                this._activeView,
                this._startCanvasCoords,
                endCanvasCoords
            );

            this.clearOverlay();

            this._startCanvasCoords = null;
            this._endCanvasCoords = null;
            this._activeView = null;
            this._ctx = null;

            if (range) {
                this._onSelect(range);
            }

            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    // ── private helpers ──────────────────────────────────────────

    /**
     * The dragged rectangle as a voxel window, or `null` for an accidental
     * click or a view the geometry refuses.
     *
     * The construction itself lives in {@link rectangleToVoxelWindow},
     * beside the single-voxel pick the viewport menu deletes a curve with,
     * so the two ways of naming a delete window resolve against one grid
     * through one refusal.
     */
    private rectangleToVoxelWindow(
        view: PlaneView,
        start: ReadonlyVec2,
        end: ReadonlyVec2
    ): VoxelSubRange | null {
        return rectangleToVoxelWindow(view, this._grid, start, end, this._mode);
    }

    /**
     * Draws a dashed rectangle on the 2D overlay canvas between the drag
     * start and the current cursor position.
     */
    private drawSelectionRect(current: ReadonlyVec2): void {
        if (!this._ctx || !this._startCanvasCoords || !this._activeView) {
            return;
        }

        const canvas = this._activeView.get2DCanvas;
        if (!canvas) {
            return;
        }

        const w = canvas.width;
        const h = canvas.height;

        this._ctx.clearRect(0, 0, w, h);

        const x = Math.min(this._startCanvasCoords[0], current[0]);
        const y = Math.min(this._startCanvasCoords[1], current[1]);
        const rw = Math.abs(current[0] - this._startCanvasCoords[0]);
        const rh = Math.abs(current[1] - this._startCanvasCoords[1]);

        this._ctx.save();
        this._ctx.setLineDash([6, 3]);
        this._ctx.strokeStyle = 'rgba(244, 152, 0, 0.9)';
        this._ctx.lineWidth = 1.5;
        this._ctx.strokeRect(x, y, rw, rh);

        this._ctx.fillStyle = 'rgba(244, 152, 0, 0.08)';
        this._ctx.fillRect(x, y, rw, rh);
        this._ctx.restore();
    }

    /**
     * Clears the selection rectangle overlay.
     */
    private clearOverlay(): void {
        if (!this._ctx || !this._activeView) {
            return;
        }
        const canvas = this._activeView.get2DCanvas;
        if (canvas) {
            this._ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }
}
